import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import type { User } from '@chatapp-inbox/shared';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage, userErrorField } from './error-message';
import { Field } from './Field';
import { PASSWORD_RULE, validatePassword } from './validation';
import { useUpdateUser } from './use-users';
import styles from './forms.module.css';
import panelStyles from './EditUserPanel.module.css';

interface UserPasswordFormProps {
  user: User;
  viewerId: string;
}

/**
 * Redefinir a senha de alguém. O servidor encerra todas as sessões da pessoa ao trocar a senha; o
 * aviso vem ANTES do envio. A senha nunca é mostrada, nem guardada além do campo, e o campo é
 * esvaziado quando dá certo.
 */
export function UserPasswordForm({ user, viewerId }: UserPasswordFormProps): ReactElement {
  const headingId = useId();
  const isYou = user.id === viewerId;
  const update = useUpdateUser(viewerId);
  const status = useStatusMessage();
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const pending = update.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    setFormError(null);
    const invalid = validatePassword(password);
    setFieldError(invalid);
    if (invalid) {
      passwordRef.current?.focus();
      return;
    }
    update.mutate(
      { id: user.id, patch: { password } },
      {
        onSuccess: (saved) => {
          setPassword('');
          // O React Query guarda as variáveis da mutação (a senha); a tela segue montada, então
          // solta-as aqui em vez de esperar a tela sair.
          update.reset();
          status.announce(
            isYou
              ? 'Senha redefinida. Você será desconectado e precisará entrar de novo.'
              : `Senha redefinida: ${saved.name}.`,
          );
        },
        onError: (error) => {
          const message = adminErrorMessage(
            error,
            'Não foi possível redefinir a senha. Tente de novo.',
          );
          if (userErrorField(error) === 'password') {
            setFieldError(message);
            passwordRef.current?.focus();
          } else {
            setFormError(message);
          }
        },
      },
    );
  };

  return (
    <section aria-labelledby={headingId} className={panelStyles.block}>
      <h4 id={headingId} className={panelStyles.blockTitle}>
        Redefinir senha
      </h4>
      <form className={styles.form} noValidate aria-busy={pending || undefined} onSubmit={submit}>
        <Field
          ref={passwordRef}
          label="Nova senha"
          type="password"
          hint={
            <>
              {PASSWORD_RULE}{' '}
              {isYou
                ? 'Atenção: ao redefinir a SUA senha, todas as suas sessões (inclusive esta) são encerradas e você precisa entrar de novo.'
                : 'A pessoa é desconectada de todos os aparelhos e entra com a senha nova.'}
            </>
          }
          autoComplete="new-password"
          autoCapitalize="none"
          spellCheck={false}
          value={password}
          readOnly={pending}
          error={fieldError}
          onChange={(event) => {
            setPassword(event.target.value);
            setFieldError(null);
          }}
        />
        {formError && (
          <p role="alert" className={styles.alert}>
            {formError}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending ? 'Redefinindo…' : 'Redefinir senha'}
          </Button>
        </div>
        <StatusRegion message={status.message} />
      </form>
    </section>
  );
}
